// ABOUTME: Lazy-loading component for insight reference sections with editing capability.
// ABOUTME: Fetches insights from backend and allows editing via DataEditorModal (copy-on-write).

import { useEffect, useState, useCallback, useRef, type RefObject } from 'react';
import { AlertCircle, Pencil, Trash2, BarChart3, Table2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { InsightRenderer } from '@/components/investigation/InsightRenderer';
import { DataEditorModal } from './DataEditorModal';
import { TableEditorModal } from './TableEditorModal';
import { apiClient } from '@/services/api';
import { messageApi } from '@/services/api/client';
import type { APIInsight } from '@/types/api';
import type { StructuredInsight } from '@/types/sdk';
import type { ChartType } from '@/utils/chartValidation';

interface TableContent {
  title: string;
  headers: string[];
  rows: (string | number)[][];
}

interface InsightReferenceSectionProps {
  reportId: string;
  messageId: string;
  insightId: string;
  sectionId: string;
  reportVersion: number;
  versionRef?: RefObject<number>;
  editable?: boolean;
  tableExpanded?: boolean;
  onTableExpandedChange?: (expanded: boolean) => Promise<void>;
  onBeforeTableSave?: () => Promise<void>;
  onConversionComplete?: () => void;
  onDelete?: () => void;
}

export function InsightReferenceSection({
  reportId,
  messageId,
  insightId,
  sectionId,
  reportVersion,
  versionRef,
  editable = true,
  tableExpanded,
  onTableExpandedChange,
  onBeforeTableSave,
  onConversionComplete,
  onDelete,
}: InsightReferenceSectionProps) {
  const [insight, setInsight] = useState<APIInsight | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditingChart, setIsEditingChart] = useState(false);
  const [isEditingTable, setIsEditingTable] = useState(false);
  const [localVersion, setLocalVersion] = useState(reportVersion);

  const [isToggling, setIsToggling] = useState(false);
  const isTogglingRef = useRef(false);

  // Local state for optimistic updates after conversion (raw Record format for multi-series support)
  const [localChartData, setLocalChartData] = useState<Record<string, unknown>[] | null>(null);
  const [localChartType, setLocalChartType] = useState<ChartType | null>(null);
  const [localTableData, setLocalTableData] = useState<TableContent | null>(null);
  const [hasConverted, setHasConverted] = useState(false);

  const getLatestVersion = useCallback(() => versionRef?.current ?? localVersion, [versionRef, localVersion]);

  useEffect(() => {
    setLocalVersion((current) => Math.max(current, reportVersion));
  }, [reportVersion]);

  useEffect(() => {
    const fetchInsight = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await messageApi.getInsight(messageId, insightId);
        setInsight(data);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        console.error('insight_load_failed', {
          messageId,
          insightId,
          error: errorMessage
        });
      } finally {
        setLoading(false);
      }
    };

    fetchInsight();
  }, [messageId, insightId]);

  // Extract chart data from insight (supports both 'chart' and 'chart-table' types)
  // Returns raw Record<string, unknown>[] to preserve multi-series structure
  const getChartDataFromInsight = useCallback((): { data: Record<string, unknown>[], chartType: ChartType, title?: string } | null => {
    if (hasConverted && localChartData) {
      return {
        data: localChartData,
        chartType: localChartType || 'bar',
        title: insight?.title
      };
    }

    // Support both 'chart' and 'chart-table' types
    if (!insight || !insight.chart || (insight.type !== 'chart' && insight.type !== 'chart-table')) {
      return null;
    }

    const rawData = insight.chart.data || [];
    if (rawData.length === 0) {
      return null;
    }

    // Pass the raw data through directly to preserve multi-series structure
    return {
      data: rawData as Record<string, unknown>[],
      chartType: (insight.chart.type as ChartType) || 'bar',
      title: insight.title
    };
  }, [insight, hasConverted, localChartData, localChartType]);

  // Extract table data from insight (supports both 'table' and 'chart-table' types)
  const getTableDataFromInsight = useCallback((): TableContent | null => {
    if (hasConverted && localTableData) {
      return localTableData;
    }

    // Support both 'table' and 'chart-table' types
    if (!insight || !insight.table || (insight.type !== 'table' && insight.type !== 'chart-table' && insight.type !== 'table-summary')) {
      return null;
    }

    const headers = (insight.table.columns || []).map(col =>
      col.header || col.key || col.id || ''
    );

    const columnIds = (insight.table.columns || []).map(col =>
      col.id || col.key || col.header || ''
    );

    const rows = (insight.table.rows || []).map((row: any) => {
      if (Array.isArray(row)) {
        return row.map(cell => cell ?? '');
      }
      return columnIds.map((colId: string) => row[colId] ?? '');
    }) as (string | number)[][];

    return {
      title: insight.title || 'Table',
      headers: headers.length > 0 ? headers : ['Column 1'],
      rows: rows.length > 0 ? rows : [['']],
    };
  }, [insight, hasConverted, localTableData]);

  // Handle chart save
  const handleChartSaveSuccess = useCallback((newVersion: number, data: Record<string, unknown>[], chartType: ChartType) => {
    setLocalChartData(data);
    setLocalChartType(chartType);
    setLocalVersion(newVersion);
    setHasConverted(true);
    onConversionComplete?.();
  }, [onConversionComplete]);

  // Handle table save - persists to backend via updateTableData API
  const handleTableSave = useCallback(async (data: TableContent) => {
    await onBeforeTableSave?.();

    // Convert editor format (headers/rows arrays) to backend TableConfig format (columns/rows objects)
    const tableConfig: Record<string, unknown> = {
      columns: data.headers.map((header, i) => ({
        id: `col_${i}`,
        header,
        type: 'text',
      })),
      rows: data.rows.map((row) => {
        const rowObj: Record<string, unknown> = {};
        row.forEach((cell, i) => {
          rowObj[`col_${i}`] = cell;
        });
        return rowObj;
      }),
      max_display_rows: insight?.table?.max_display_rows ?? data.rows.length,
    };

    const response = await apiClient.reports.updateTableData(
      reportId,
      sectionId,
      tableConfig,
      { ifMatch: getLatestVersion() }
    );

    setLocalTableData(data);
    setLocalVersion(response.version);
    setHasConverted(true);
    onConversionComplete?.();
  }, [reportId, sectionId, getLatestVersion, insight?.table, onBeforeTableSave, onConversionComplete]);

  const handleEditChartClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditingChart(true);
  }, []);

  const handleEditTableClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditingTable(true);
  }, []);

  // Default click handler - for pure chart/table opens editor, for chart-table does nothing (use specific buttons)
  const handleContentClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (insight?.type === 'chart') {
      setIsEditingChart(true);
    } else if (insight?.type === 'table' || insight?.type === 'table-summary') {
      setIsEditingTable(true);
    }
    // For chart-table, don't auto-open - user must click specific edit button
  }, [insight?.type]);

  const handleTableToggle = useCallback(async (expanded: boolean) => {
    if (!onTableExpandedChange || isTogglingRef.current) return;
    isTogglingRef.current = true;
    setIsToggling(true);
    try {
      await onTableExpandedChange(expanded);
    } finally {
      isTogglingRef.current = false;
      setIsToggling(false);
    }
  }, [onTableExpandedChange]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 space-x-3">
        <LoadingSpinner size="md" />
        <span className="text-sm text-surface-500">
          Loading visualization...
        </span>
      </div>
    );
  }

  if (error || !insight) {
    return (
      <div className="flex items-center justify-center py-8 space-x-3 text-error">
        <AlertCircle className="w-5 h-5" />
        <div className="flex flex-col">
          <span className="text-sm font-medium">
            Unable to load visualization
          </span>
          {error && (
            <span className="text-xs text-surface-500 mt-1">{error}</span>
          )}
        </div>
      </div>
    );
  }

  const chartData = getChartDataFromInsight();
  const tableData = getTableDataFromInsight();
  const canEdit = editable && (chartData !== null || tableData !== null);

  // Convert localTableData (editor format) back to TableConfig (renderer format)
  // Editor format: { title, headers: string[], rows: (string|number)[][] }
  // Renderer format: { columns: [{id, header}], rows: [{colId: value}] }
  const resolvedTable = (() => {
    if (hasConverted && localTableData) {
      const columns = localTableData.headers.map((header: string, i: number) => ({
        id: `col_${i}`,
        header,
        type: 'text' as const,
      }));
      const rows = localTableData.rows.map((row: (string | number)[]) => {
        const rowObj: Record<string, unknown> = {};
        row.forEach((cell, i) => {
          rowObj[`col_${i}`] = cell;
        });
        return rowObj;
      });
      return {
        columns,
        rows,
        max_display_rows: insight.table?.max_display_rows ?? rows.length,
        show_row_numbers: insight.table?.show_row_numbers,
      };
    }
    return insight.table;
  })();

  // Convert APIInsight to StructuredInsight for InsightRenderer
  const structuredInsight: StructuredInsight = {
    id: insight.id,
    type: insight.type,
    title: insight.title,
    insight_overview: insight.insight_overview,
    selection_reasoning: insight.selection_reasoning || {
      primary_reason: 'Referenced in report',
      data_characteristics: [],
      confidence: 'high' as const,
      alternatives_considered: [],
      assumptions: [],
    },
    chart: insight.chart as any,
    table: resolvedTable as any,
    summary: insight.summary as any,
    key_metrics: insight.key_metrics as any,
    smart_actions: insight.smart_actions as any,
    python_viz: insight.python_viz as any,
  };

  return (
    <>
      <motion.div
        layout
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="insight-reference-section group relative"
        data-section-id={sectionId}
      >
        {/* Content */}
        <div
          className={`relative rounded-sm bg-surface-0 border border-surface-200 transition-colors overflow-hidden ${editable ? 'hover:border-surface-300 cursor-pointer' : ''}`}
          onClick={editable ? handleContentClick : undefined}
        >
          {/* Inline Edit/Delete Controls */}
          <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
            {/* For chart-table type, show separate edit buttons */}
            {editable && insight.type === 'chart-table' && chartData && (
              <button
                onClick={handleEditChartClick}
                className="p-1.5 rounded-sm bg-surface-50 text-surface-500 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                title="Edit Chart"
              >
                <BarChart3 className="w-3.5 h-3.5" />
              </button>
            )}
            {editable && insight.type === 'chart-table' && tableData && (
              <button
                onClick={handleEditTableClick}
                className="p-1.5 rounded-sm bg-surface-50 text-surface-500 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                title="Edit Table"
              >
                <Table2 className="w-3.5 h-3.5" />
              </button>
            )}
            {/* For pure chart or table type, show single edit button */}
            {editable && insight.type !== 'chart-table' && canEdit && (
              <button
                onClick={insight.type === 'chart' ? handleEditChartClick : handleEditTableClick}
                className="p-1.5 rounded-sm bg-surface-50 text-surface-500 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                title="Edit"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
            {onDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                className="p-1.5 rounded-sm bg-surface-50 text-surface-500 hover:text-error hover:bg-error-light transition-colors cursor-pointer"
                title="Delete"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Conversion Notice */}
          {hasConverted && (
            <div className="mx-4 mt-4 px-2.5 py-1.5 bg-primary-50 border border-primary-200 rounded-sm inline-block">
              <span className="text-[10px] text-primary-600">
                Converted to inline data
              </span>
            </div>
          )}

          {/* Collapse target: when table_expanded=false, TableInsight shows `max_display_rows || 10` rows.
             This is consistent with table/chart sections which collapse to 10 via onMaxDisplayRowsChange. */}
          <InsightRenderer insight={structuredInsight} minimal={true} showSmartActions={false} hideSummary={true} tableExpanded={tableExpanded} onTableExpandedChange={handleTableToggle} toggleDisabled={isToggling} />
        </div>
      </motion.div>

      {/* Chart Data Editor Panel */}
      {chartData && (
        <DataEditorModal
          isOpen={isEditingChart}
          onClose={() => setIsEditingChart(false)}
          reportId={reportId}
          sectionId={sectionId}
          initialData={chartData.data}
          initialChartType={chartData.chartType}
          initialTitle={chartData.title}
          version={getLatestVersion()}
          onSaveSuccess={handleChartSaveSuccess}
        />
      )}

      {/* Table Editor Panel */}
      {tableData && (
        <TableEditorModal
          isOpen={isEditingTable}
          onClose={() => setIsEditingTable(false)}
          initialData={tableData}
          onSave={handleTableSave}
        />
      )}
    </>
  );
}
